import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { vtNavigate } from '../lib/viewTransition.js';
import Icon from './Icon.jsx';

// Home row: Owl Analyst Activity — how much the team leaned on the AI analyst this
// week, and the last six things they opened a conversation about.
//
// Reads ONE small endpoint (/api/my/analyst-usage/:id/home — three vendor queries,
// cached), not the full report: this loads on every visit to the home page, for
// everyone. The server answers { snapshot: null } for every "nothing to show" case
// — analyst not linked, flag off, vendor unreachable — and the row then renders
// nothing at all, so a vendor outage can never break somebody's home page.
//
// The question wording rides the same `ai.analyst_questions` switch as the report.
// With it off the row still earns its place: the count, who asked and when are all
// still there, and each line says so rather than showing a blank.
export default function AnalystActivityRow({ entityId, isMobile }) {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    if (!entityId) { setSnap(null); return; }
    let alive = true;
    setSnap(null);
    api.myAnalystHome(entityId)
      .then((r) => { if (alive) setSnap(r.snapshot || null); })
      .catch(() => { /* decorative row — stay silent and render nothing */ });
    return () => { alive = false; };
  }, [entityId]);

  return <AnalystRowView snap={snap} isMobile={isMobile} onOpen={() => vtNavigate(navigate, '/analyst-activity')} />;
}

// The row itself, separated from the fetching so every state it can be handed is
// renderable in a test — a static render never runs effects, so the component
// above can only ever be exercised empty.
export function AnalystRowView({ snap, isMobile, onOpen }) {
  // Nothing used it this week → no row. A standing "0 questions" panel on the home
  // page is noise, and the report is one click away in the nav for anyone curious.
  if (!snap || !snap.questions) return null;

  const open = onOpen || (() => {});
  const recent = snap.recent || [];

  return (
    <>
      <h2 style={head}>
        <span><Icon name="owl" size={15} /></span>Owl Analyst Activity
        <span style={faint}>what your team asked the analyst this week</span>
        <button onClick={open} style={headLink}>Full report →</button>
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px 1fr', gap: 12, alignItems: 'start' }}>
        <button className="lift" style={tile} onClick={open}>
          <div style={tileLbl}>Questions this week</div>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, marginTop: 2 }}>{Number(snap.questions).toLocaleString()}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
            from {snap.people} {snap.people === 1 ? 'person' : 'people'}
            {snap.organisers ? ` · ${snap.organisers} ${snap.organisers === 1 ? 'organiser' : 'organisers'}` : ''}
          </div>
          <span style={tileGo}>View →</span>
        </button>

        <div style={listBox}>
          {recent.map((c) => (
            <button key={c.id} className="lift" style={line} onClick={open}>
              <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                {snap.questionsShown ? (
                  <span style={askedLine}>
                    {c.asked || <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(opened without a message)</span>}
                  </span>
                ) : null}
                <span style={metaLine}>
                  {c.name || 'Someone'} · {c.senderType === 'Internal user' ? 'Staff' : 'Organiser'} · {when(c.at)} · {c.answers} {c.answers === 1 ? 'answer' : 'answers'}
                </span>
              </span>
              <span style={{ color: 'var(--brand)', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>→</span>
            </button>
          ))}
          {!snap.questionsShown && (
            <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '6px 2px 0', lineHeight: 1.5 }}>
              The wording of each question is switched off for this account — ask Howler to turn on “what was asked”.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// "2 days ago" reads better than a date in a list that only spans a week.
function when(at) {
  if (!at) return '';
  const d = Math.floor((Date.now() - new Date(at).getTime()) / 86400000);
  if (!Number.isFinite(d)) return '';
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d} days ago`;
}

const head = { fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em', margin: '22px 0 10px', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' };
const faint = { fontWeight: 400, fontSize: 11, color: 'var(--muted)' };
const headLink = { marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: 'inherit' };
const tile = { position: 'relative', textAlign: 'left', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '12px 14px 30px', cursor: 'pointer', font: 'inherit', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };
const tileLbl = { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)' };
const tileGo = { position: 'absolute', left: 14, bottom: 10, fontSize: 12, fontWeight: 700, color: 'var(--brand)' };
const listBox = { border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '4px 12px 10px' };
const line = { display: 'flex', gap: 10, alignItems: 'center', width: '100%', minHeight: 44, padding: '9px 0', background: 'none', border: 'none', borderBottom: '1px solid var(--hairline)', cursor: 'pointer', font: 'inherit', color: 'var(--text)' };
const askedLine = { display: 'block', fontSize: 13, fontWeight: 600, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const metaLine = { display: 'block', fontSize: 11.5, color: 'var(--muted)', marginTop: 2 };
